import {
  AddThreadAction,
  ConversationHoverActions,
  CreateTaskAction,
  ForwardAction,
  PinAction,
  ReplyAction,
} from '@modules/conversation-message/ConversationHoverActions';
import { ConversationMessageContent } from '@modules/conversation-message/ConversationMessageContent';
import { ConversationMessageOptions } from '@modules/conversation-message/ConversationMessageOptions';
import { ConversationMessageReactions } from '@modules/conversation-message/ConversationMessageReactions';
import { ConversationMessageReply } from '@modules/conversation-message/ConversationMessageReply';
import { useForwardMessageDialog } from '@modules/conversation/ForwardMessageModal';
import { useMessageMediaContent } from '@modules/conversation/state/ConversationMedia';
import { useDownload } from '@modules/file-menagement';
import { CircularProgress, IconButton, styled } from '@mui/material';
import type { Editor } from '@tiptap/react';
import classNames from 'classnames';
import cn from 'classnames';
import { bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { memo, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ChatMessageListItem, IChatConversation } from 'src/@types/chat';
import { ConversationMessageContentType } from 'src/@types/conversation';
import { UserType } from 'src/@types/workspace-info';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { useAuthContext } from 'src/auth/useAuthContext';
import { EmojiPopover } from 'src/components/EmojiPickerPopover/EmojiPopover.js';
import Iconify from 'src/components/iconify/Iconify.js';
import { useWorkspaceUser } from 'src/hooks/useWorkspace';
import { ChatMessageThread } from 'src/sections/@dashboard/chat/ChatConversation/ChatMessageList/ChatMessageThread';
import { useChatConversationContext } from 'src/sections/@dashboard/chat/ChatConversation/useChatConversationContext';
import { useMessageReactions } from 'src/sections/@dashboard/chat/ChatConversation/useMessageReactions';
import { ChatMessageEditInput } from 'src/sections/@dashboard/chat/shared/ChatMessageInput/ChatMessageEditInput';
import { ForwardChip } from 'src/sections/@dashboard/chat/shared/ForwardedChip';
import { formatMessageTime } from '../shared/utils';
import { useDeleteMessage } from '../useDeleteMessage';
import { usePinMessage } from '../usePinMessage';
import { ChatMessageAuthorDisplay } from './ChatMessageAuthorDisplay';

type Props = {
  message: ChatMessageListItem;
  conversation: IChatConversation;
  hideAuthor?: boolean;
  disableActions?: boolean;
  editor: Editor;
  updateMessage?: (message: ChatMessageListItem) => void;
};

function ChatMessageItemInner({
  message,
  editor,
  conversation,
  hideAuthor,
  disableActions,
  updateMessage,
}: Props) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isOpened, setIsOpened] = useState(false);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  // const messageContentRef = useRef<HTMLDivElement>(null);

  //TODO: add popover to create task with title based on selection
  // const [selection, setSelection] = useState<Selection | null>(null);
  // useEffect(() => {
  //   const handleSelectionChange = () => {
  //     const selection = window.getSelection();
  //     if (!selection || selection.isCollapsed) {
  //       setSelection(null);
  //       return;
  //     }
  //     const isInMessage =
  //       selection.anchorNode &&
  //       selection.focusNode &&
  //       messageContainerRef.current &&
  //       messageContainerRef.current.contains(selection.anchorNode) &&
  //       messageContainerRef.current.contains(selection.focusNode);

  //     setSelection(!!isInMessage ? selection : null);
  //   };
  //   document.addEventListener('selectionchange', handleSelectionChange);
  //   return () =>
  //     document.removeEventListener('selectionchange', handleSelectionChange);
  // }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();

  const { setReplyTo, goToMessage } = useChatConversationContext();
  const { mediaContent, getMediaContent } = useMessageMediaContent(message);

  const { deleteMessage } = useDeleteMessage({
    channelId: conversation._id,
    id: message._id,
  });
  const { forwardMessage } = useForwardMessageDialog();
  const { pinMessage } = usePinMessage({ channelId: conversation._id });
  const { downloadContent, loadingMap } = useDownload();

  const { reactMessage } = useMessageReactions({
    channelId: conversation._id,
    goToMessage,
  });

  const sender = useWorkspaceUser(message.author);

  const optionState = usePopupState({
    variant: 'popover',
    popupId: 'demoPopover',
  });

  const handleDeleteMessage = async () => {
    await deleteMessage({ channel: conversation._id, id: message._id });
  };

  const senderDetails = useMemo(() => {
    const name = sender?.name ?? '';
    const integration = sender?.type === UserType.INTEGRATION;

    const firstName = integration ? name : name.split(' ')[0];

    return {
      picture: sender?.picture,
      name,
      integration,
      firstName,
    };
  }, [sender]);

  const handleSendReaction = async (emoji: string) => {
    if (!emoji) return;
    await reactMessage({
      messageId: message._id,
      channelId: conversation._id,
      reaction: emoji,
    });
  };

  const handleAddThread = () => {
    navigate(PATH_DASHBOARD.chat.newThread(message.channel, message._id), {
      state: { background: location, initialMessage: message },
    });
  };

  const handleTogglePinMessage = async () => {
    if (updateMessage) {
      updateMessage({
        ...message,
        pinned: !message.pinned,
      });
    }
    await pinMessage(message);
  };

  const handleReplyMessage = (msg: ChatMessageListItem) => {
    setReplyTo(msg);
    editor.commands.focus();
  };

  const handleDownload = async () => {
    const mediaFile = await getMediaContent(message);
    downloadContent({
      fileName: message.fileName || `messageFile.${message.mimeType}`,
      blob: mediaFile,
    });
  };

  return (
    <StyledSpacerContainer className={cn({ sameAuthor: hideAuthor })}>
      <StyledContainer
        className={classNames({
          isOpened,
          isOptionsOpen: optionState.isOpen,
          editing: isEditing,
        })}
      >
        {!disableActions && (
          <ConversationHoverActions>
            {message.contentType === ConversationMessageContentType.TEXT &&
              message.content && (
                <CreateTaskAction
                  message={message}
                  conversation={conversation}
                  isOpened={setIsOpened}
                  getCurrentHtml={() => editor.getHTML()}
                />
              )}
            <EmojiPopover
              onSelectEmoji={(emoji) => handleSendReaction(emoji.shortcodes)}
              arrow="right-center"
              icon="fluent:emoji-add-16-regular"
              isOpened={setIsOpened}
            />
            {!conversation.parentId && !conversation.p2p && (
              <AddThreadAction
                onClick={() => {
                  handleAddThread();
                }}
              />
            )}
            <PinAction
              pinned={message.pinned}
              onClick={handleTogglePinMessage}
            />
            <ReplyAction
              onClick={() => {
                handleReplyMessage(message);
              }}
            />
            <ForwardAction
              onClick={(e) => {
                forwardMessage(e, message);
              }}
            />
            {(user?._id === sender?._id ||
              message.contentType !== ConversationMessageContentType.TEXT) && (
              <>
                {loadingMap[message.fileName as string] ? (
                  <CircularProgress size={24} />
                ) : (
                  <IconButton size="small" {...bindTrigger(optionState)}>
                    <Iconify icon="tabler:dots" />
                  </IconButton>
                )}

                <ConversationMessageOptions
                  isAuthor={user?._id === sender?._id}
                  state={optionState}
                  onDelete={handleDeleteMessage}
                  onDownload={mediaContent ? handleDownload : undefined}
                  onEdit={() => setIsEditing(true)}
                />
              </>
            )}
          </ConversationHoverActions>
        )}

        <StyledWrapper
          className={classNames({
            highlight: goToMessage === message._id,
            noAuthor: hideAuthor,
          })}
          sx={{ position: 'relative' }}
          ref={messageContainerRef}
        >
          <ChatMessageAuthorDisplay
            sender={sender}
            hideAuthor={hideAuthor}
            message={message}
          />

          <div style={{ overflow: 'hidden' }}>
            {!hideAuthor && sender && (
              <StyledSenderName>
                {senderDetails.firstName}
                {senderDetails.integration && <BotBadge>BOT</BotBadge>}
                <StyledMessageTime>
                  {formatMessageTime(message.createdAt)}
                </StyledMessageTime>
              </StyledSenderName>
            )}

            {message.replyTo?._id && (
              <ConversationMessageReply
                message={message.replyTo}
                editor={editor}
              />
            )}
            {message.forwarded && <ForwardChip />}

            {isEditing ? (
              <ChatMessageEditInput
                message={message}
                cancelEdition={() => setIsEditing(false)}
              />
            ) : (
              <ConversationMessageContent
                message={message}
                editor={editor}
                // ref={messageContentRef}
              />
            )}

            {!!message.reactions?.length && (
              <ConversationMessageReactions
                reactions={message.reactions}
                sendReaction={handleSendReaction}
              />
            )}
          </div>
        </StyledWrapper>
      </StyledContainer>

      {/* <ChatMessageTableRowMention tableRow={message.relatedTableRow} /> */}

      {!!message.threadMessagesCount && !conversation?.parentId && (
        <ChatMessageThread
          message={message}
          conversation={conversation}
          editor={editor}
        />
      )}
    </StyledSpacerContainer>
  );
}

export const ChatMessageItem = memo(
  ChatMessageItemInner,
  (prevProps, nextProps) =>
    prevProps.message._id === nextProps.message._id &&
    prevProps.message.threadLastMessage ===
      nextProps.message.threadLastMessage &&
    prevProps.message.pinned === nextProps.message.pinned &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.reactions === nextProps.message.reactions &&
    prevProps.message.mediaContent === nextProps.message.mediaContent,
);

const StyledSpacerContainer = styled('div')(({ theme }) => ({
  '&:not(.sameAuthor)': {
    paddingTop: theme.spacing(1),
  },
}));

const StyledContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  '.message-actions': {
    visibility: 'hidden',
  },

  '.hoverShow': {
    visibility: 'hidden',
  },

  '.ProseMirror': {
    padding: 0,
  },
  '&:hover, &.isOptionsOpen, &.isOpened': {
    backgroundColor: theme.palette.background.neutral,

    '.hoverShow': {
      visibility: 'visible',
    },
    '&:not(.editing) .message-actions': {
      visibility: 'visible',
    },
  },
}));

const StyledWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '56px 1fr',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1, 0.25),
  '&.highlight': {
    backgroundColor: theme.palette.background.paper,
  },
  '&.noAuthor': {
    paddingTop: theme.spacing(0.25),
    marginTop: theme.spacing(0),
    alignItems: 'center',
  },
}));

const StyledMessageTime = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

const StyledSenderName = styled('p')(({ theme }) => ({
  ...theme.typography.subtitle2,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),

  margin: 0,
  padding: 0,
  whiteSpace: 'nowrap',
}));

const BotBadge = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.15, 0.75),
  fontSize: theme.typography.caption.fontSize,
  marginLeft: theme.spacing(0.1),
  display: 'inline-block',
}));
